import { Rights, User, useUserLazyQuery, useUsersQuery } from '../graphql/types';
import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import ProfileAvatar from '../sections/profiles/ProfileAvatar';
import { Autocomplete, ListItemAvatar, ListItemButton, ListItemText, Stack, TextField, Typography } from '@mui/material';
import { getUserProfileFullName } from '../utils/users';

interface InvestorDropDownAutoCompleteProps {
  onInvestorChange: (selectedUser: Partial<User> | null) => void;
  rights?: Rights[];
  investorFilterId?: string | undefined;
  usersToFilterOut?: User[];
}

const InvestorDropDownAutoComplete: React.FC<InvestorDropDownAutoCompleteProps> = (props) => {
  const { rights, onInvestorChange, investorFilterId, usersToFilterOut } = props;
  let rightsFilter = rights;
  if (!rightsFilter || !rightsFilter.length) {
    rightsFilter = [Rights.IsInvestor, Rights.IsSuperAdmin, Rights.IsBroker, Rights.IsIsoManager, Rights.IsFunder,Rights.IsUser];
  }
  const [users, setUsers] = useState<readonly User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userInputValue, setUserInputValue] = useState<string>('');
  const {
    loading: usersLoading,
    data: usersData,
    refetch: usersRefetch
  } = useUsersQuery({
    variables: { rights: rightsFilter, isActive: true },
    fetchPolicy: 'network-only'
  });
  const [fetchUserById, { data: userByIdData }] = useUserLazyQuery();
  const filteredUsers = useMemo(() => {
    const allUsers = (usersData?.users.data as User[]) || [];
    return allUsers.filter((user) => !usersToFilterOut?.some((userToFilterOut) => userToFilterOut.id === user.id));
  }, [usersData?.users.data, usersToFilterOut]);

  useEffect(() => {
    setUsers(filteredUsers);
  }, [filteredUsers]);

  const debouncedUserFetch = useMemo(
    () =>
      debounce((input: string) => {
        usersRefetch({ search: input, rights }).then(({ data }) => {
          const fetchedUsers = (data?.users.data as User[]) || [];
          setUsers(fetchedUsers.filter((user) => !usersToFilterOut?.some((u) => u.id === user.id)));
        });
      }, 200),
    [usersRefetch, usersToFilterOut, rights]
  );

  useEffect(() => {
    if (userInputValue) debouncedUserFetch(userInputValue);
  }, [userInputValue, debouncedUserFetch]);
  const isIsoManagerOnly = rights && rights.length === 1 && rights.includes(Rights.IsIsoManager);

  useEffect(() => {
    if (investorFilterId && !selectedUser) {
      const existingUser = users.find((user) => user.id === investorFilterId);
      if (existingUser) {
        setSelectedUser(existingUser);
        onInvestorChange(existingUser);
      } else {
        fetchUserById({ variables: { id: investorFilterId } }).then(({ data }) => {
          if (data?.user) {
            setSelectedUser(data.user);
            onInvestorChange(data.user);
          }
        });
      }
    }
  }, [investorFilterId, users, selectedUser, fetchUserById, onInvestorChange]);

  const UserListOption = ({ user, ...props }) => {
    return (
      <ListItemButton {...props}>
        <ListItemAvatar sx={{ marginRight: '-20px', marginLeft: '-8px' }}>
          <ProfileAvatar user={user} size="xs" />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Stack component="span" direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography
                color="inherit"
                sx={{
                  overflow: 'hidden',
                  fontSize: '12px',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {getUserProfileFullName(user)}
              </Typography>
              <Typography component="span" color="primary.main" variant="caption">
                  {user.role?.join(' / ')}
              </Typography>
            </Stack>
          }
          secondary={
            <Stack component="span" direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'gray'
                }}
              >
                {user.email}
              </Typography>
            </Stack>
          }
        />
      </ListItemButton>
    );
  };

  return (
    <Autocomplete
      id="syndication-entity-user"
      fullWidth
      getOptionLabel={(option: Partial<User>) => getUserProfileFullName(option as User)}
      options={users}
      autoComplete
      filterOptions={(options) => options}
      value={selectedUser}
      isOptionEqualToValue={(option: Partial<User>, value: Partial<User>) => option.id === value.id}
      onChange={(event: SyntheticEvent, newValue: Partial<User> | null) => {
        setSelectedUser(newValue || null);
        onInvestorChange(newValue ? newValue.id : null);
        props.onInvestorChange(newValue);
      }}
      onInputChange={(event: SyntheticEvent, newInputValue: string, reason) => {
        setUserInputValue(newInputValue);
        debouncedUserFetch(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          name="user"
          placeholder={isIsoManagerOnly ? 'Search Manager...' : 'Search User...'}
          size="small"
          sx={{ minWidth: '280px' }}
          InputProps={{
            ...params.InputProps,
            startAdornment: selectedUser?.id && <ProfileAvatar user={selectedUser as User} size={'xs'} />
          }}
        />
      )}
      renderOption={(props, option) => <UserListOption user={option as User} {...props} />}
    />
  );
};
export default InvestorDropDownAutoComplete;
